/**
 * Street Dog Co. — Dynamic Collection Pin
 * ----------------------------------------
 * Pins a specific product to the first slot of the collection grid when
 * the page URL includes a `first` parameter (product handle, or a full
 * product URL like Facebook's catalog deep links send).
 *
 * URL params:
 *   ?first=<handle-or-product-url>   (activates and refreshes the pin)
 *
 * Persistence:
 *   Once a pin is set via ?first=, the product handle AND the current
 *   collection's handle are stored together in sessionStorage. Later page
 *   loads *without* ?first= in the URL (e.g. clicking around the site,
 *   then returning) will still apply the pin, but ONLY if the visitor is
 *   back on that SAME collection — visiting a different collection won't
 *   inherit the pin. Clears automatically when the browser tab/session
 *   ends — it's meant to last for that visit, not permanently.
 *
 * Behaviour:
 *  1. If the product is already rendered in the grid, move it to slot #1.
 *     No network request — instant, no flicker.
 *  2. If it's NOT already rendered (large collections, product ranked
 *     below the page size), use the product data already being fetched by
 *     the early speculative request (see main-collection.liquid) — or
 *     fetch it now if that's not available — CLONE an existing real
 *     product card (so it uses the theme's actual <product-card> custom
 *     element and CSS — not hand-built markup that can render broken/
 *     blank), swap in the new product's content, and prepend it.
 *
 * Speed: the speculative fetch in main-collection.liquid starts the
 * network request the instant the page begins loading — well before this
 * deferred script even runs — so by the time we need the data, it's
 * usually already arrived or close to it.
 *
 * Anti-flicker: only slot #1 is hidden (via CSS on :first-child) while
 * resolving — the rest of the grid renders normally immediately. This
 * script reveals slot #1 once the pin resolves (success, failure, or a
 * safety timeout), so the visible "hidden" window is both shorter (thanks
 * to the early fetch) and far less disruptive (rest of the page is never
 * blanked).
 */
(() => {
  const STORAGE_KEY = 'streetdog_pinned_handle';
  const REVEAL_SAFETY_TIMEOUT_MS = 2500;
  let revealed = false;

  function reveal() {
    if (revealed) return;
    revealed = true;
    document.documentElement.classList.remove('curated-first-loading');
  }

  window.setTimeout(reveal, REVEAL_SAFETY_TIMEOUT_MS);

  function getHandleFromParam(raw) {
    if (!raw) return null;
    try {
      if (raw.indexOf('http') === 0 || raw.indexOf('%2F') !== -1 || raw.indexOf('/products/') !== -1) {
        const decoded = decodeURIComponent(raw);
        const match = decoded.match(/\/products\/([a-zA-Z0-9\-_.]+)/);
        if (match) return match[1];
      }
    } catch (e) {
      /* fall through to treating raw as a plain handle */
    }
    return raw;
  }

  function getCurrentCollectionHandle() {
    return window.STREETDOG_COLLECTION_HANDLE || null;
  }

  function getStoredHandle() {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const currentCollection = getCurrentCollectionHandle();
      // Only honor the stored pin if we're back on the SAME collection it
      // was set for — prevents it from following the visitor to unrelated
      // collections where the product may not even fit.
      if (parsed && parsed.collectionHandle === currentCollection) {
        return parsed.handle || null;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function setStoredHandle(handle) {
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ handle: handle, collectionHandle: getCurrentCollectionHandle() })
      );
    } catch (e) {
      /* sessionStorage unavailable (private browsing, etc.) — pin still
         works for this page view via the URL param, just won't persist. */
    }
  }

  function moneyFormat(cents) {
    try {
      const currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function prioritizeImages(li) {
    li.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      try {
        img.fetchPriority = 'high';
      } catch (e) {
        img.setAttribute('fetchpriority', 'high');
      }
    });
  }

  async function fetchProductJSON(handle) {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error('Product fetch failed: ' + res.status);
    return res.json();
  }

  // Reuse the speculative fetch kicked off early in main-collection.liquid,
  // if one is in flight for this exact handle. Falls back to a fresh fetch
  // otherwise (e.g. if the early script couldn't run for some reason).
  function getProductData(handle) {
    if (
      window.__streetdogPinHandle === handle &&
      window.__streetdogPinFetchPromise &&
      typeof window.__streetdogPinFetchPromise.then === 'function'
    ) {
      return window.__streetdogPinFetchPromise;
    }
    return fetchProductJSON(handle);
  }

  function walkTextNodes(root, callback) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach(callback);
  }

  function buildClonedCard(templateLi, product) {
    const originalTitleEl = templateLi.querySelector('a.product-card__link .visually-hidden');
    const originalTitleText = originalTitleEl ? originalTitleEl.textContent.trim() : null;

    const priceRegex = /\$[\d,]+\.\d{2}/g;
    const originalPrices = Array.from(new Set((templateLi.textContent.match(priceRegex) || [])));

    const clone = templateLi.cloneNode(true);
    clone.removeAttribute('id');
    clone.setAttribute('data-product-id', product.id);
    clone.setAttribute('data-pinned-fallback', 'true');
    clone.className = 'product-grid__item product-grid__item--0';

    clone.querySelectorAll('a[href]').forEach((a) => {
      a.setAttribute('href', product.url);
    });

    const newImage = product.featured_image || (product.images && product.images[0]) || '';
    if (newImage) {
      clone.querySelectorAll('img').forEach((img) => {
        img.setAttribute('src', newImage);
        img.removeAttribute('srcset');
        img.setAttribute('alt', product.title);
      });
    }

    if (originalTitleText) {
      walkTextNodes(clone, (node) => {
        if (node.textContent.trim() === originalTitleText) {
          node.textContent = node.textContent.replace(originalTitleText, product.title);
        }
      });
    }

    const newPrice = moneyFormat(product.price);
    const newCompareAt =
      product.compare_at_price && product.compare_at_price > product.price
        ? moneyFormat(product.compare_at_price)
        : null;

    originalPrices.forEach((oldPriceStr, i) => {
      const replacement = i === 0 ? newPrice : newCompareAt || newPrice;
      walkTextNodes(clone, (node) => {
        if (node.textContent.includes(oldPriceStr)) {
          node.textContent = node.textContent.replace(oldPriceStr, replacement);
        }
      });
    });

    return clone;
  }

  async function pinFirstProduct() {
    const params = new URLSearchParams(window.location.search);
    const rawFirst = params.get('first');

    let handle = null;

    if (rawFirst) {
      // A fresh ?first= always wins, and refreshes what's stored for the
      // rest of this session.
      handle = getHandleFromParam(rawFirst);
      if (handle) setStoredHandle(handle);
    } else {
      // No ?first= on this page load — fall back to whatever was pinned
      // earlier in this browsing session, if anything.
      handle = getStoredHandle();
    }

    if (!handle) {
      reveal();
      return;
    }

    const grid = document.querySelector('ul.product-grid[ref="grid"]') || document.querySelector('ul.product-grid');
    if (!grid) {
      reveal();
      return;
    }

    const items = Array.from(grid.children).filter((el) => el.tagName === 'LI');
    if (items.length === 0) {
      reveal();
      return;
    }

    // --- Fast path: product already rendered in this page's grid ---
    const existing = items.find((li) => {
      const link = li.querySelector('a.product-card__link, a[href*="/products/"]');
      return link && link.getAttribute('href') && link.getAttribute('href').indexOf(`/products/${handle}`) !== -1;
    });

    if (existing) {
      if (grid.firstElementChild !== existing) {
        grid.prepend(existing);
      }
      prioritizeImages(existing);
      reveal();
      return;
    }

    // --- Fallback path: use (or start) the product fetch, clone a real card, swap content ---
    try {
      const product = await getProductData(handle);
      const templateLi = items[0];
      const card = buildClonedCard(templateLi, product);
      prioritizeImages(card);
      grid.prepend(card);
    } catch (err) {
      console.warn('[pin-first-product] could not pin product:', handle, err);
    } finally {
      reveal();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pinFirstProduct);
  } else {
    pinFirstProduct();
  }
})();
